import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Box,
  Chip,
} from '@mui/material';
import {
  gray80,
  gray100,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { GROUP_EMPTY_MESSAGE } from '../../../constants/labels';
import type { GroupType } from '../../../types/group';

type GroupListCardProps = {
  groups: GroupType[];
  /** ユーザーUID → 表示名（メンバーバッジ表示用） */
  memberNameMap: Record<string, string>;
  onEdit: (groupId: string) => void;
  onDelete: (group: GroupType) => void;
};

const styles = {
  paper: {
    width: '100%',
    padding: '20px',
    backgroundColor: '#FFF',
    border: '1px solid #E0E0E0',
    boxShadow: 'none',
    boxSizing: 'border-box',
  },
  tableContainer: {
    overflowX: 'auto',
    width: '100%',
    WebkitOverflowScrolling: 'touch',
    '& table': { minWidth: '720px' },
  },
  headerCell: {
    padding: '6.5px',
    color: gray80,
    fontSize: '14px',
    fontWeight: 400,
    whiteSpace: 'nowrap',
  },
  nameCell: {
    padding: '6.5px',
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    minWidth: '160px',
    whiteSpace: 'nowrap',
  },
  descCell: {
    padding: '6.5px',
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    maxWidth: '280px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  memberCell: { padding: '6.5px', color: gray100, fontSize: '14px', minWidth: '240px' },
  actionCell: { padding: '6.5px', minWidth: '160px', whiteSpace: 'nowrap' },
  buttonHeight: { height: '32px' },
  deleteButtonMargin: { marginLeft: '16px' },
};

/**
 * グループ一覧テーブル（#2804）
 * 担当者一覧（StaffListCard）と同じテーブル表示に統一。
 * メンバー列は所属メンバー名をバッジ表示し、押下で担当者編集へ遷移する。
 */
const GroupListCard: React.FC<GroupListCardProps> = ({
  groups,
  memberNameMap,
  onEdit,
  onDelete,
}) => {
  return (
    <Paper sx={styles.paper}>
      <TableContainer sx={styles.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={styles.headerCell}>グループ名</TableCell>
              <TableCell sx={styles.headerCell}>説明</TableCell>
              <TableCell sx={styles.headerCell}>メンバー</TableCell>
              <TableCell sx={styles.headerCell}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map(group => {
              const memberIds = group.member_ids.filter(id => memberNameMap[id]);
              return (
                <TableRow key={group.id}>
                  <TableCell sx={styles.nameCell}>{group.name}</TableCell>
                  <TableCell sx={styles.descCell}>{group.description || '-'}</TableCell>
                  <TableCell sx={styles.memberCell}>
                    {memberIds.length === 0 ? (
                      '-'
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {memberIds.map(id => (
                          <Chip
                            key={id}
                            label={memberNameMap[id]}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={styles.actionCell}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        sx={[secondaryButtonBackgroundColor, styles.buttonHeight]}
                        onClick={(): void => onEdit(group.id)}
                      >
                        編集
                      </Button>
                      <Button
                        sx={[
                          tertiaryButtonBackgroundColor,
                          styles.deleteButtonMargin,
                          styles.buttonHeight,
                        ]}
                        onClick={(): void => onDelete(group)}
                      >
                        削除
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {groups.length === 0 && (
        <Typography sx={{ ...styles.headerCell, textAlign: 'center', padding: '24px' }}>
          {GROUP_EMPTY_MESSAGE}
        </Typography>
      )}
    </Paper>
  );
};

export default GroupListCard;
